import { Box, ListItemIcon, Typography, useColorScheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { editor } from 'monaco-editor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FrameworkClasses } from '../../../framework/frameworkClasses';
import { getFrameworkSensorClassNames } from '../../../utils/frameworkClassNameSets';
import { collectLastTopLevelVarAssignmentsByName, splitCodeLines } from '../../../utils/pythonSourceScanUtils';
import type { ActionItem } from '../../../redux/EditorSlice';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { isDarkColorScheme } from '../../../utils/colorScheme';
import { electronics } from '../../../utils/constants';
import { getBitsElectronicIcon } from '../../../utils/getBitsElectronicIcon';
import { ActionMappingControlList } from '../../Drawers/Controller/ActionMappingControlList';
import BeamBreakerSvg from '../../Drawers/assets/electrical-explorer/beam-breaker.svg';
import DistanceSensorSvg from '../../Drawers/assets/electrical-explorer/distance-sensor.svg';
import MagneticProximitySwitchSvg from '../../Drawers/assets/electrical-explorer/magnetic-proximity-switch.svg';
import SensorMappingDialog, {
  type SensorActionDraft,
  type SensorMappingDraft,
  type SensorTriggerOption,
  type TriggerDraft,
} from './SensorMappingDialog';
import { readSensorMappingDraftFromCode, upsertSensorMappingInCode } from './sensorCodeUtils';
import { SENSOR_ACTION_SIDES, type SensorActionSide } from './sensorMappingConstants';

interface SensorConfigurationTabProps {
  editor?: editor.IStandaloneCodeEditor;
}

type SensorInProgram = { variableName: string; className: string };

function normalizeSensorAction(action: Partial<SensorActionDraft> | null | undefined): SensorActionDraft | null {
  const actionLabel = String(action?.actionLabel ?? '').trim();
  if (!actionLabel) return null;
  return {
    actionLabel,
    argValues: (action?.argValues ?? {}) as Record<string, string>,
  };
}

function getSensorActionsForSide(
  trigger: TriggerDraft,
  side: typeof SENSOR_ACTION_SIDES.ON_TRUE | typeof SENSOR_ACTION_SIDES.ON_FALSE
): SensorActionDraft[] {
  if (side === SENSOR_ACTION_SIDES.ON_TRUE) {
    if (Array.isArray(trigger.onTrueActions)) {
      return trigger.onTrueActions.map((action) => normalizeSensorAction(action)).filter((action): action is SensorActionDraft => action !== null);
    }
    const legacyAction = normalizeSensorAction({ actionLabel: trigger.onTrueActionLabel, argValues: trigger.onTrueArgValues });
    return legacyAction ? [legacyAction] : [];
  }

  if (Array.isArray(trigger.onFalseActions)) {
    return trigger.onFalseActions.map((action) => normalizeSensorAction(action)).filter((action): action is SensorActionDraft => action !== null);
  }
  const legacyAction = normalizeSensorAction({ actionLabel: trigger.onFalseActionLabel, argValues: trigger.onFalseArgValues });
  return legacyAction ? [legacyAction] : [];
}

function withSensorLegacyActionFields(trigger: TriggerDraft): TriggerDraft {
  const onTrueFirst = getSensorActionsForSide(trigger, SENSOR_ACTION_SIDES.ON_TRUE)[0];
  const onFalseFirst = getSensorActionsForSide(trigger, SENSOR_ACTION_SIDES.ON_FALSE)[0];
  return {
    ...trigger,
    onTrueActionLabel: onTrueFirst?.actionLabel ?? '',
    onTrueArgValues: onTrueFirst?.argValues ?? {},
    onFalseActionLabel: onFalseFirst?.actionLabel ?? '',
    onFalseArgValues: onFalseFirst?.argValues ?? {},
  };
}

function removeSensorActionAt(
  trigger: TriggerDraft,
  side: typeof SENSOR_ACTION_SIDES.ON_TRUE | typeof SENSOR_ACTION_SIDES.ON_FALSE,
  actionIndex: number
): TriggerDraft {
  const nextTrigger: TriggerDraft = { ...trigger };
  const actions = getSensorActionsForSide(trigger, side);
  const safeIndex = Math.max(0, Number(actionIndex || 0));
  if (safeIndex < actions.length) {
    actions.splice(safeIndex, 1);
  }

  if (side === SENSOR_ACTION_SIDES.ON_TRUE) {
    nextTrigger.onTrueActions = actions;
  } else {
    nextTrigger.onFalseActions = actions;
  }

  return withSensorLegacyActionFields(nextTrigger);
}

const SENSOR_CLASS_TO_ELECTRONIC_TYPE: Partial<Record<string, string>> = {
  BeamBreakSensor: electronics.beamBreakSensor.ELECTRONIC_TYPE,
  DistanceSensor: electronics.distanceSensor.ELECTRONIC_TYPE,
  MagneticProximitySwitch: electronics.magneticSwitch.ELECTRONIC_TYPE,
};

const SENSOR_CLASS_TO_LARGE_SVG: Partial<Record<string, string>> = {
  BeamBreakSensor: BeamBreakerSvg,
  DistanceSensor: DistanceSensorSvg,
  MagneticProximitySwitch: MagneticProximitySwitchSvg,
};

const SensorConfigurationTab: React.FC<SensorConfigurationTabProps> = ({ editor }) => {
  const { t } = useTranslation();
  const programCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const globalActionList = useAppSelector((state) => state.editor.actionList) as ActionItem[];
  const { mode, systemMode } = useColorScheme();
  const isDarkMode = isDarkColorScheme(mode, systemMode);

  const sensorClassNames = useMemo(() => getFrameworkSensorClassNames(), []);

  const sensorsInProgram = useMemo(() => {
    const code = String(programCode || '');
    if (!code.trim()) return [];

    return collectLastTopLevelVarAssignmentsByName(splitCodeLines(code), {
      allowedClassNames: sensorClassNames,
    }).map(({ variableName, className }) => ({ variableName, className }));
  }, [programCode, sensorClassNames]);

  const [sensorDialogOpen, setSensorDialogOpen] = useState(false);
  const [initialSensorVarName, setInitialSensorVarName] = useState<string | null>(null);
  const [initialTriggerIndex, setInitialTriggerIndex] = useState<number | null>(null);
  const [initialActionSide, setInitialActionSide] = useState<SensorActionSide | null>(null);
  const [initialActionIndex, setInitialActionIndex] = useState<number | null>(null);

  const [sensorDraftsByVar, setSensorDraftsByVar] = useState<Record<string, SensorMappingDraft>>({});

  const sensorOptions = useMemo(() => {
    return (sensorsInProgram || []).map((s) => {
      const fc = FrameworkClasses.find((c) => c.name === s.className);
      const st = (fc?.sensorTriggers ?? []) as SensorTriggerOption[];
      return { sensorVarName: s.variableName, sensorTriggerOptions: st };
    });
  }, [sensorsInProgram]);

  const refreshDraftFromCode = useCallback(
    (sensorVarName: string): SensorMappingDraft | null => {
      if (!editor) return null;
      const fromCode = readSensorMappingDraftFromCode({ editor, sensorVarName });
      if (!fromCode) {
        // If the user deletes the mapping statements directly in the editor, we should clear any
        // cached UI draft so the list doesn't show a "ghost" mapping that no longer exists in code.
        setSensorDraftsByVar((prev) => {
          if (!prev[sensorVarName]) return prev;
          const next = { ...prev };
          delete next[sensorVarName];
          return next;
        });
        return null;
      }
      setSensorDraftsByVar((prev) => {
        const prevDraft = prev[sensorVarName];
        const prevJson = prevDraft ? JSON.stringify(prevDraft) : '';
        const nextJson = JSON.stringify(fromCode);
        if (prevJson === nextJson) return prev;
        return { ...prev, [sensorVarName]: fromCode };
      });
      return fromCode;
    },
    [editor]
  );

  // Keep mapping list in sync with current code for all sensors.
  useEffect(() => {
    if (!editor) return;
    if ((sensorsInProgram || []).length === 0) return;
    sensorsInProgram.forEach((s) => refreshDraftFromCode(s.variableName));
  }, [editor, refreshDraftFromCode, sensorsInProgram]);

  // Remove stale local drafts when sensors are removed from code.
  useEffect(() => {
    const activeSensors = new Set((sensorsInProgram || []).map((s) => s.variableName));
    setSensorDraftsByVar((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([sensorVarName]) => activeSensors.has(sensorVarName)));
      if (Object.keys(next).length === Object.keys(prev).length) return prev;
      return next;
    });
  }, [sensorsInProgram]);

  const openSensorDialog = (sensor: SensorInProgram) => {
    refreshDraftFromCode(sensor.variableName);
    setInitialSensorVarName(sensor.variableName);
    setInitialTriggerIndex(null);
    setInitialActionSide(null);
    setInitialActionIndex(null);
    setSensorDialogOpen(true);
  };

  const openSensorDialogByVarName = useCallback(
    (sensorVarName: string, triggerIndex?: number | null, actionSide?: SensorActionSide | null, actionIndex?: number | null) => {
      const sensor = (sensorsInProgram || []).find((s) => s.variableName === sensorVarName);
      if (!sensor) return;
      refreshDraftFromCode(sensor.variableName);
      setInitialSensorVarName(sensor.variableName);
      if (typeof triggerIndex === 'number') {
        setInitialTriggerIndex(triggerIndex);
        setInitialActionSide(actionSide ?? null);
        setInitialActionIndex(typeof actionIndex === 'number' ? actionIndex : null);
      } else {
        setInitialTriggerIndex(null);
        setInitialActionSide(null);
        setInitialActionIndex(null);
      }
      setSensorDialogOpen(true);
    },
    [refreshDraftFromCode, sensorsInProgram]
  );

  const closeSensorDialog = () => {
    setSensorDialogOpen(false);
    setInitialSensorVarName(null);
    setInitialTriggerIndex(null);
    setInitialActionSide(null);
    setInitialActionIndex(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      {sensorsInProgram.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body2" color="text.secondary">
            {t('configurationDrawer.noActiveElectronics')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
            {t('controlPanel.sensors.selector.helperText', {
              defaultValue: 'Select an active sensor below to define input actions',
            })}
          </Typography>

          <Grid container spacing={2}>
            {(sensorsInProgram || []).map((sensor) => {
              const typeTitle = sensor.className;

              const electronicType = SENSOR_CLASS_TO_ELECTRONIC_TYPE[sensor.className] ?? '';
              const SmallIcon = getBitsElectronicIcon(electronicType);
              const largeSvgSrc = SENSOR_CLASS_TO_LARGE_SVG[sensor.className];
              const variableName = sensor.variableName;

              return (
                <Grid key={sensor.variableName} size={{ xs: 6 }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => openSensorDialog(sensor)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') openSensorDialog(sensor);
                    }}
                    sx={(theme) => ({
                      cursor: 'pointer',
                      borderRadius: 2,
                      bgcolor: isDarkMode ? theme.vars?.palette?.action?.selected : theme.vars?.palette?.background?.paper,
                      border: `1px solid ${theme.vars?.palette?.divider}`,
                      boxShadow: theme.shadows[2],
                      px: 2,
                      py: 2,
                      outline: 'none',
                      transition: 'background-color 120ms ease, box-shadow 120ms ease',
                      '&:hover': {
                        bgcolor: isDarkMode ? theme.vars?.palette?.action?.focus : theme.vars?.palette?.action?.hover,
                        boxShadow: theme.shadows[4],
                      },
                      '&:focus-visible': {
                        boxShadow: theme.shadows[6],
                      },
                    })}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      {largeSvgSrc ? (
                        <Box
                          component="img"
                          src={largeSvgSrc}
                          alt={typeTitle}
                          sx={{
                            width: '100%',
                            maxWidth: 140,
                            height: 90,
                            objectFit: 'contain',
                            filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0)',
                          }}
                        />
                      ) : null}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, overflow: 'hidden' }}>
                      {SmallIcon ? (
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <Box
                            component={SmallIcon}
                            aria-hidden
                            sx={{
                              width: 18,
                              height: 18,
                              display: 'block',
                              color: 'primary.main',
                            }}
                          />
                        </ListItemIcon>
                      ) : null}
                      <Box sx={{ overflow: 'hidden', width: '100%' }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 12,
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                          }}
                        >
                          {variableName}
                        </Typography>
                        <Typography
                          noWrap
                          color="text.secondary"
                          sx={{
                            fontSize: 12,
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                          }}
                        >
                          {typeTitle}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <ActionMappingControlList
        variant="sensor"
        sensorVarNames={(sensorOptions || []).map((s) => s.sensorVarName)}
        draftsBySensorVar={sensorDraftsByVar}
        onAddSensorMapping={(sensorVarName) => {
          openSensorDialogByVarName(sensorVarName, null);
        }}
        onEditSensor={(sensorVarName, triggerIndex, side, actionIndex) =>
          openSensorDialogByVarName(sensorVarName, triggerIndex, side ?? null, actionIndex ?? null)
        }
        onDeleteSensorMapping={(sensorVarName, triggerIndex, side, actionIndex) => {
          const currentDraft = sensorDraftsByVar[sensorVarName];
          if (!currentDraft) return;
          const currTriggers = currentDraft.triggers || [];
          const tr = currTriggers[triggerIndex];
          if (!tr) return;

          const nextTriggers = [...currTriggers];
          const nextTr = removeSensorActionAt(tr, side, actionIndex);

          const hasTrue = getSensorActionsForSide(nextTr, SENSOR_ACTION_SIDES.ON_TRUE).length > 0;
          const hasFalse = getSensorActionsForSide(nextTr, SENSOR_ACTION_SIDES.ON_FALSE).length > 0;
          if (!hasTrue && !hasFalse) {
            nextTriggers.splice(triggerIndex, 1);
          } else {
            nextTriggers[triggerIndex] = nextTr;
          }

          const nextDraft: SensorMappingDraft = { triggers: nextTriggers };
          setSensorDraftsByVar((prev) => {
            const prevDraft = prev[sensorVarName];
            if (!prevDraft) return prev;
            if (nextTriggers.length === 0) {
              const next = { ...prev };
              delete next[sensorVarName];
              return next;
            }
            return { ...prev, [sensorVarName]: nextDraft };
          });
          if (editor) {
            upsertSensorMappingInCode({
              editor,
              sensorVarName,
              draft: nextDraft,
              actionList: globalActionList || [],
            });
          }
        }}
      />

      <SensorMappingDialog
        open={sensorDialogOpen}
        onClose={closeSensorDialog}
        onSave={({ sensorVarName, draft }, options) => {
          if (!sensorVarName) return;
          setSensorDraftsByVar((prev) => ({ ...prev, [sensorVarName]: draft }));
          if (editor) {
            upsertSensorMappingInCode({
              editor,
              sensorVarName,
              draft,
              actionList: globalActionList || [],
            });
          }
          if (options?.closeDialog !== false) {
            closeSensorDialog();
          }
        }}
        sensorOptions={sensorOptions}
        initialSensorVarName={initialSensorVarName}
        initialTriggerIndex={initialTriggerIndex}
        initialActionSide={initialActionSide}
        initialActionIndex={initialActionIndex}
        initialDraftsBySensorVar={sensorDraftsByVar}
        onRefreshDraftFromCode={refreshDraftFromCode}
      />
    </Box>
  );
};

export default SensorConfigurationTab;
